import { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bookmark, ChevronDown, Filter, MapPin, Search as SearchIcon } from 'lucide-react';
import { uuidv7 } from 'uuidv7';
import { api, APIError } from '../api/client';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { clearAuthSession, getUserId } from '../utils/auth';
import { toggleSaveJob, isJobSaved } from '../utils/savedJobs';
import { pushDataLayer } from '../utils/dataLayer';
import { COUNTRIES, DEFAULT_COUNTRY, getCountryCode, getLanguageHint } from '../config/countries';

const metaByIndex = [
  { age: '2 days ago', applicants: '45 applicants' },
  { age: '1 day ago', applicants: '128 applicants' },
  { age: '3 days ago', applicants: '67 applicants' },
  { age: '5 days ago', applicants: '23 applicants' },
];

export default function ResultsScreen() {
  const navigate = useNavigate();
  const authModal = useAuthModal();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [savedJobIds, setSavedJobIds] = useState(new Set());

  // Search form state
  const [selectedCountries, setSelectedCountries] = useState([DEFAULT_COUNTRY]);
  const [jobKeywords, setJobKeywords] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);

  // Advanced filters state
  const [datePosted, setDatePosted] = useState('all');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [jobRequirements, setJobRequirements] = useState('');
  const [radius, setRadius] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isMobileSearchExpanded, setIsMobileSearchExpanded] = useState(false);

  const intervalRef = useRef(null);
  const countryMenuRef = useRef(null);


  const handleCountryToggle = (country) => {
    // Теперь всегда выбирается только одна страна
    setSelectedCountries([country]);
    setIsCountryMenuOpen(false); // Закрываем меню после выбора
  };

  const handleEmploymentTypeToggle = (type) => {
    setEmploymentTypes(prev => {
      if (prev.includes(type)) return prev.filter(t => t !== type);
      return [...prev, type];
    });
  };

  const stripHtmlTags = (html) => {
    if (!html) return '';
    return String(html).replace(/<[^>]*>/g, '').trim();
  };

  const getWorkMode = (location = '') => {
    const lower = location.toLowerCase();
    if (lower.includes('remote')) return 'Remote';
    if (lower.includes('hybrid')) return 'Hybrid';
    return 'On-site';
  };

  const getTags = (job) => {
    const title = (job.title || '').toLowerCase();
    const tags = [];
    if (title.includes('product')) tags.push('Product Management');
    if (title.includes('data')) tags.push('Data');
    if (title.includes('analyst') || title.includes('analytics')) tags.push('Analytics');
    if (title.includes('ux')) tags.push('UX');
    if (tags.length === 0) tags.push('General');
    return tags.slice(0, 3);
  };

  const checkRequestStatus = async (requestId) => {
    try {
      console.log(`[Polling] Checking status for request_id: ${requestId}`);

      const data = await api.get(`/api/jobs_search_requests/status/${requestId}`);
      console.log(`[Polling] Status response:`, data);

      // Если статус успешный (status === '2' или 2), возвращаем true
      if (data.status === '2' || data.status === 2) {
        console.log(`[Polling] Status is SUCCESS (2), fetching results`);
        return true;
      }

      console.log(`[Polling] Status is ${data.status}, continue waiting...`);
      return false;
    } catch (err) {
      if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
        console.log('[Polling] Unauthorized, clearing auth session and showing auth modal');
        clearAuthSession();
        authModal.openAuth('search');
        return 'unauthorized';
      }
      console.error('[Polling] Status check error:', err);
      return false;
    }
  };

  const handleSearch = async () => {
    // Проверка авторизации в первую очередь
    const userId = getUserId();
    const isAuthorized = !!userId;

    pushDataLayer({
      event: 'job_search_click',
      location: 'vacancies',
      country: selectedCountries?.[0] || '',
      user_id: userId ? String(userId) : null,
      filters: {
        is_text: jobKeywords.trim() ? 1 : 0,
        is_date: datePosted !== 'all' ? 1 : 0,
        is_employed_type: employmentTypes.length > 0 ? 1 : 0,
        is_remoute: remoteOnly ? 1 : 0,
        is_experience: jobRequirements.trim() ? 1 : 0,
        is_radius: radius ? 1 : 0,
      },
      data_source: 'front',
    });

    if (!isAuthorized) {
      console.log('[Search] User not authorized, showing auth modal');
      authModal.openAuth('search');
      return;
    }

    if (!jobKeywords.trim()) {
      console.log('[Search] Keywords empty, returning');
      return;
    }

    console.log('[Search] Starting search...');
    setSearchLoading(true);
    setCanLoadMore(true);

    try {
      // Генерируем уникальный UUID v7 для этого запроса
      const requestId = uuidv7();
      console.log('[Search] Generated request_id:', requestId);

      // Авторизованный пользователь - отправляем реальные данные
      const selectedCountry = selectedCountries.length > 0 ? selectedCountries[0] : DEFAULT_COUNTRY;
      const countryCode = getCountryCode(selectedCountry);

      const requestBody = {
        query: jobKeywords.trim(),
        country_code: countryCode,
        request_id: requestId,
        // New filters
        date_posted: datePosted,
        employment_types: employmentTypes.join(','),
        remote_jobs_only: remoteOnly,
        job_requirements: jobRequirements,
        radius: radius ? parseInt(radius) : null
      };
      console.log('[Search] Request body:', requestBody);

      // Отправляем POST запрос
      console.log('[Search] Sending POST request...');
      await api.post('/api/jobs_search_requests/search', requestBody);
      console.log('[Search] POST request successful');

      // Запускаем проверку статуса каждые 5 секунд
      console.log('[Search] Starting polling every 5 seconds...');
      const statusCheckInterval = setInterval(async () => {
        const status = await checkRequestStatus(requestId);

        if (status === 'unauthorized') {
          clearInterval(statusCheckInterval);
          setSearchLoading(false);
          return;
        }

        if (status) {
          // Очищаем интервал
          clearInterval(statusCheckInterval);

          // Сохраняем request_id в localStorage для получения результатов
          localStorage.setItem('lastSearchRequestId', requestId);
          console.log('[Search] Saved request_id to localStorage, fetching results');

          // Загружаем результаты
          await fetchResults();
          setSearchLoading(false);
        }
      }, 5000); // Проверяем каждые 5 секунд

      // Сохраняем ссылку на интервал для возможности очистки
      intervalRef.current = statusCheckInterval;

    } catch (err) {
      if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
        console.log('[Search] Unauthorized response, clearing auth session and showing auth modal');
        clearAuthSession();
        authModal.openAuth('search');
        setSearchLoading(false);
        return;
      }
      console.error('[Search] Search error:', err);
      setSearchLoading(false);
    }
  };

  const fetchResults = async () => {
    setLoading(true);
    try {
      const userId = getUserId();
      if (!userId) {
        setLoading(false);
        authModal.openAuth('search');
        return;
      }

      const requestId = localStorage.getItem('lastSearchRequestId');
      if (!requestId) {
        setLoading(false);
        return;
      }

      const data = await api.get(`/api/jobs_search_requests/${requestId}/result`);

      if (data.jobs && Array.isArray(data.jobs)) {
        const formattedJobs = data.jobs.map((job, index) => ({
          id: requestId + '-' + index,
          title: stripHtmlTags(job.title) || '',
          company: stripHtmlTags(job.company) || '',
          location: job.locations || '',
          salary: job.salary || '',
          description: stripHtmlTags(job.description) || '',
          job_id: job.job_id || '',
        }));
        setJobs(formattedJobs);
        localStorage.setItem('currentJobs', JSON.stringify(formattedJobs));

        // Initialize saved jobs state
        const savedIds = new Set();
        formattedJobs.forEach((job) => {
          if (isJobSaved(job.id)) {
            savedIds.add(job.id);
          }
        });
        setSavedJobIds(savedIds);
        if (formattedJobs.length === 0) {
          setCanLoadMore(false);
        }
      }
    } catch (err) {
      if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
        clearAuthSession();
        authModal.openAuth('search');
        setLoading(false);
        return;
      }
      console.error('[Results] Results fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (loadingMore || !canLoadMore) return;

    const userId = getUserId();
    if (!userId) {
      authModal.openAuth('search');
      return;
    }

    const requestId = localStorage.getItem('lastSearchRequestId');
    if (!requestId) return;

    setLoadingMore(true);
    try {
      const data = await api.post(`/api/jobs_search_requests/${requestId}/load-more`);

      if (!data || typeof data.added_count !== 'number') {
        setCanLoadMore(false);
        return;
      }

      if (data.added_count > 0) {
        await fetchResults();
      } else {
        setCanLoadMore(false);
      }
    } catch (err) {
      if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
        clearAuthSession();
        authModal.openAuth('search');
        setLoadingMore(false);
        return;
      }
      console.error('[Results] Load more error:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Очищаем интервал при размонтировании компонента
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryMenuRef.current && !countryMenuRef.current.contains(event.target)) {
        setIsCountryMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchResults();
  }, []);

  const handleToggleSave = (job) => {
    const isSaved = toggleSaveJob(job);
    setSavedJobIds((prev) => {
      const newSet = new Set(prev);
      if (isSaved) {
        newSet.add(job.id);
      } else {
        newSet.delete(job.id);
      }
      return newSet;
    });
  };

  const jobCountLabel = useMemo(() => {
    if (loading) return 'Loading results...';
    if (jobs.length === 0) return 'No jobs found';
    return `${jobs.length} jobs found`;
  }, [jobs.length, loading]);

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        <section className="flex justify-center">
          <div className="inline-flex items-center rounded-full border border-gray-200 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => navigate('/chat-setting')}
              className="rounded-full px-6 py-2 text-sm font-semibold text-gray-500 transition hover:text-gray-700"
            >
              Interview
            </button>
            <button
              type="button"
              className="rounded-full bg-orange-500 px-6 py-2 text-sm font-semibold text-white shadow-sm"
            >
              Vacancy
            </button>
          </div>
        </section>

        <section className="md:hidden">
          <button
            type="button"
            onClick={() => setIsMobileSearchExpanded(true)}
            className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 flex items-center gap-3"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-50">
              <SearchIcon className="h-4 w-4 text-orange-500" />
            </span>
            <span className="min-w-0 flex-1 text-left">
              <span className="block truncate text-sm font-semibold text-slate-900">
                {jobKeywords.trim() || 'Search query'} · {selectedCountries[0] || 'Country'}
              </span>
              <span className="block text-[11px] text-gray-500">
                {jobCountLabel} · {getLanguageHint(selectedCountries[0])}
              </span>
            </span>
            <span className="text-xs font-semibold text-orange-600">Edit</span>
          </button>

          {isMobileSearchExpanded && (
            <div className="mt-2 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-800">
                    Role or skills{' '}
                    <span className="text-orange-600 font-medium">
                      ({getLanguageHint(selectedCountries[0])})
                    </span>
                  </label>
                  <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-3">
                    <SearchIcon className="h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={jobKeywords}
                      onChange={(e) => setJobKeywords(e.target.value)}
                      placeholder="e.g. Product Analyst"
                      className="w-full bg-transparent py-2.5 text-sm outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-800">Country</label>
                  <div className="relative">
                    <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <select
                      value={selectedCountries[0] || DEFAULT_COUNTRY}
                      onChange={(e) => handleCountryToggle(e.target.value)}
                      className="w-full appearance-none rounded-xl border border-gray-200 py-2.5 pl-9 pr-9 text-sm text-gray-700 outline-none"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.label} value={c.label}>{c.flag} {c.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={searchLoading}
                    className="btn-orange flex-1 py-2.5 text-sm disabled:opacity-50"
                  >
                    {searchLoading ? 'Searching...' : 'Search Jobs'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsMobileSearchExpanded(false)}
                    className="rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-3 px-1 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">{jobs.length} jobs found</p>
            <button type="button" className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600">
              Most Relevant
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </div>
        </section>

        <section className="hidden md:flex flex-col md:flex-row md:items-center md:justify-between gap-4 rounded-3xl border border-gray-200 bg-white px-6 py-5 shadow-sm">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Vacancies</h1>
            <p className="text-sm text-gray-500 mt-1">{jobCountLabel}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="flex items-center gap-2 border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button
              type="button"
              className="flex items-center gap-2 border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Most Relevant
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </section>

        {/* Search Form */}
        <section className="hidden md:block bg-white rounded-[28px] border border-gray-200 shadow-sm p-6 md:p-8">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white">1</div>
            <span className="text-xs font-bold uppercase tracking-[0.08em] text-slate-700">Search Filters</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Role or skills{' '}
                <span className="text-orange-500 font-normal">
                  ({getLanguageHint(selectedCountries[0])})
                </span>
              </label>
              <div className="flex items-center gap-3 border border-gray-200 rounded-2xl px-4 py-3">
                <SearchIcon className="w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="e.g. Product Analyst"
                  className="flex-1 text-sm outline-none bg-transparent"
                  value={jobKeywords}
                  onChange={(e) => setJobKeywords(e.target.value)}
                />
              </div>
            </div>

            <div className="lg:col-span-2 relative" ref={countryMenuRef}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Country
              </label>
              <button
                type="button"
                onClick={() => setIsCountryMenuOpen((prev) => !prev)}
                className="w-full flex items-center justify-between gap-3 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-600 hover:border-gray-300"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {selectedCountries.length > 0 ? selectedCountries.join(', ') : 'Select country'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {isCountryMenuOpen && (
                <div className="absolute top-full left-0 z-50 mt-2 w-full rounded-2xl border border-gray-200 bg-white shadow-lg p-4 flex flex-wrap gap-2">
                  {COUNTRIES.map((c) => (
                    <button
                      key={c.label}
                      type="button"
                      onClick={() => handleCountryToggle(c.label)}
                      className={`px-4 py-2 rounded-2xl text-sm font-medium transition ${selectedCountries.includes(c.label)
                        ? 'border-2 border-blue-500 bg-blue-50 text-blue-500'
                        : 'border border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                        }`}
                    >
                      {c.flag} {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-1 flex items-end">
              <button
                type="button"
                onClick={handleSearch}
                disabled={searchLoading}
                className="btn-orange w-full text-sm py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {searchLoading ? 'Searching...' : 'Search jobs'}
              </button>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white">2</div>
              <span className="text-xs font-bold uppercase tracking-[0.08em] text-slate-700">Advanced Filters</span>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm font-semibold text-orange-600 mb-4 hover:underline"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
            </button>

            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fadeIn">
                {/* Date Posted */}
                <div className="space-y-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Date Posted</span>
                  <select
                    value={datePosted}
                    onChange={(e) => setDatePosted(e.target.value)}
                    className="w-full p-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500"
                  >
                    <option value="all">Any time</option>
                    <option value="today">Today</option>
                    <option value="3days">Last 3 days</option>
                    <option value="week">Last week</option>
                    <option value="month">Last month</option>
                  </select>
                </div>

                {/* Employment Type */}
                <div className="space-y-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Employment Type</span>
                  <div className="flex flex-col gap-2">
                    {[
                      { value: 'FULLTIME', label: 'Full-time' },
                      { value: 'CONTRACTOR', label: 'Contractor' },
                      { value: 'PARTTIME', label: 'Part-time' },
                      { value: 'INTERN', label: 'Intern' }
                    ].map(type => (
                      <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={employmentTypes.includes(type.value)}
                          onChange={() => handleEmploymentTypeToggle(type.value)}
                          className="rounded text-blue-500 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Requirements & Remote */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Experience</span>
                    <select
                      value={jobRequirements}
                      onChange={(e) => setJobRequirements(e.target.value)}
                      className="w-full p-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500"
                    >
                      <option value="">Any experience</option>
                      <option value="under_3_years_experience">Under 3 years</option>
                      <option value="more_than_3_years_experience">More than 3 years</option>
                      <option value="no_experience">No experience</option>
                      <option value="no_degree">No degree</option>
                    </select>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer p-2 border border-dashed border-gray-300 rounded-xl hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={remoteOnly}
                      onChange={(e) => setRemoteOnly(e.target.checked)}
                      className="rounded text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Remote Work Only</span>
                  </label>
                </div>

                {/* Radius */}
                <div className="space-y-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Radius (km)</span>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="10"
                      value={radius || 0}
                      onChange={(e) => setRadius(e.target.value)}
                      className="flex-1"
                    />
                    <span className="text-sm font-mono text-gray-600 min-w-[3ch]">
                      {radius ? `${radius}km` : 'Auto'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">Distance from selected location</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {loading ? (
          <div className="bg-white rounded-[24px] p-6 text-center text-gray-500">
            Loading results...
          </div>
        ) : jobs.length === 0 ? (
          <div className="bg-white rounded-[24px] p-6 text-center text-gray-500">
            No results found
          </div>
        ) : (
          <div className="space-y-6">
            {jobs.map((job, index) => {
              const meta = metaByIndex[index % metaByIndex.length];
              const tags = getTags(job);
              const workMode = getWorkMode(job.location);
              const companyInitial = (job.company || job.title || 'J')[0].toUpperCase();
              const isSaved = savedJobIds.has(job.id);

              return (
                <div
                  key={job.id}
                  className="bg-white rounded-2xl md:rounded-[28px] p-4 md:p-6 shadow-sm border border-gray-200 md:border-transparent"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl bg-gradient-to-br from-orange-400 to-orange-500 text-white flex items-center justify-center text-sm md:text-base font-semibold">
                        {companyInitial}
                      </div>
                      <div>
                        <p className="text-xs md:text-sm font-semibold text-gray-800">
                          {job.company || 'Company name'}
                        </p>
                        <div className="flex items-center gap-1.5 md:gap-2 text-[11px] md:text-xs text-gray-500 mt-1">
                          <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4" />
                          <span>{job.location || 'Location'}</span>
                          <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-500 text-[10px] md:text-xs">
                            {workMode}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleToggleSave(job)}
                      className={`h-9 w-9 p-0 rounded-lg md:rounded-xl border transition-colors ${isSaved
                          ? 'border-orange-500 bg-orange-50 text-orange-500'
                          : 'border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                        }`}
                      aria-label={isSaved ? 'Unsave job' : 'Save job'}
                    >
                      <Bookmark className={`mx-auto w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
                    </button>
                  </div>

                  <h2 className="text-base md:text-xl font-semibold mt-3 md:mt-4">
                    <Link
                      to={`/details/${job.id}`}
                      className="hover:text-orange-600 hover:underline transition-colors"
                    >
                      {job.title || 'Job title'}
                    </Link>
                  </h2>

                  <p className="text-xs md:text-sm text-gray-600 mt-2 md:mt-3">
                    {job.description?.length > 400
                      ? `${job.description.substring(0, 400)}...`
                      : (job.description || 'Job description will appear here.')}
                    {job.description && (
                      <span
                        className="text-[#FF7A45] cursor-pointer hover:underline"
                        onClick={() => navigate(`/details/${job.id}`)}
                      >
                        Read more
                      </span>
                    )}
                  </p>

                  <div className="mt-3 md:mt-4 flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span
                        key={`${job.id}-${tag}`}
                        className="px-2.5 md:px-3 py-1 rounded-full border border-gray-200 text-[10px] md:text-xs text-gray-600"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 md:mt-5 border-t border-gray-100 pt-3 md:pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3 md:gap-4 text-[11px] md:text-xs text-gray-400">
                      <span>{meta.age}</span>
                      <span>{meta.applicants}</span>
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/details/${job.id}`)}
                        className="btn-orange px-5 py-2 rounded-2xl text-sm"
                      >
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {canLoadMore && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="btn-orange px-6 py-2.5 rounded-2xl text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? 'Loading...' : 'Show more jobs'}
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
}
